import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const CATEGORY = z.enum([
  "bug",
  "confusing_screen",
  "content",
  "child_reaction",
  "billing",
  "suggestion",
]);
const STATUS = z.enum(["new", "reviewed", "resolved"]);
const PRIORITY = z.enum(["low", "medium", "high"]);

const SubmitSchema = z.object({
  category: CATEGORY,
  message: z.string().trim().min(1).max(4000),
  route: z.string().max(500).nullish(),
  user_agent: z.string().max(500).nullish(),
  locale: z.string().max(10).nullish(),
  app_version: z.string().max(50).nullish(),
  child_id: z.string().uuid().nullish(),
});

export const submitFeedback = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => SubmitSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase.from("beta_feedback").insert({
      user_id: userId,
      category: data.category,
      message: data.message,
      route: data.route ?? null,
      user_agent: data.user_agent ?? null,
      locale: data.locale ?? null,
      app_version: data.app_version ?? null,
      child_id: data.child_id ?? null,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const ListSchema = z.object({
  status: STATUS.nullish(),
  priority: PRIORITY.nullish(),
  limit: z.number().int().min(1).max(200).default(100),
});

export const listFeedback = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => ListSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    let q = supabase
      .from("beta_feedback")
      .select(
        "id,user_id,child_id,category,message,route,user_agent,locale,app_version,status,priority,assignee_id,internal_notes,created_at,updated_at",
      )
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (data.status) q = q.eq("status", data.status);
    if (data.priority) q = q.eq("priority", data.priority);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);

    // Enrich with parent emails via service-role admin client
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const ids = Array.from(new Set((rows ?? []).map((r) => r.user_id).filter(Boolean)));
    const emails: Record<string, string | null> = {};
    await Promise.all(
      ids.map(async (id) => {
        try {
          const { data: u } = await supabaseAdmin.auth.admin.getUserById(id);
          emails[id] = u?.user?.email ?? null;
        } catch {
          emails[id] = null;
        }
      }),
    );
    const enriched = (rows ?? []).map((r) => ({
      ...r,
      user_email: emails[r.user_id] ?? null,
    }));
    return { rows: enriched };
  });

const UpdateSchema = z.object({
  id: z.string().uuid(),
  status: STATUS.nullish(),
  priority: PRIORITY.nullish(),
});

export const updateFeedback = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => UpdateSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const patch: { status?: z.infer<typeof STATUS>; priority?: z.infer<typeof PRIORITY> } = {};
    if (data.status) patch.status = data.status;
    if (data.priority) patch.priority = data.priority;
    if (Object.keys(patch).length === 0) return { ok: true };
    const { error } = await supabase
      .from("beta_feedback")
      .update(patch)
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const NoteSchema = z.object({
  id: z.string().uuid(),
  note: z.string().trim().min(1).max(2000),
});

export const addFeedbackNote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => NoteSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId, claims } = context;
    const { data: row, error: readErr } = await supabase
      .from("beta_feedback")
      .select("internal_notes")
      .eq("id", data.id)
      .single();
    if (readErr) throw new Error(readErr.message);
    const prev = Array.isArray(row?.internal_notes) ? row.internal_notes : [];
    const next = [
      ...prev,
      {
        at: new Date().toISOString(),
        by: userId,
        by_email: (claims as { email?: string } | null)?.email ?? null,
        note: data.note,
      },
    ];
    const { error } = await supabase
      .from("beta_feedback")
      .update({ internal_notes: next })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const feedbackCount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const { count, error } = await supabase
      .from("beta_feedback")
      .select("*", { count: "exact", head: true })
      .eq("status", "new");
    if (error) throw new Error(error.message);
    return { count: count ?? 0 };
  });

const DeleteSchema = z.object({ id: z.string().uuid() });

export const deleteFeedback = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => DeleteSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    // RLS restricts delete to super admin (can_manage_roles)
    const { error } = await supabase.from("beta_feedback").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });


